import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowClockwise, PaperPlaneTilt, Sparkle, CheckCircle, X as XIcon,
  Newspaper, TrendUp, Eye, Info,
} from "@phosphor-icons/react";
import client from "../lib/api";

function fmtUSD(n) { return `$${Math.round(n || 0).toLocaleString()}`; }

export default function AdminDigestPage() {
  const [loading, setLoading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null);
  const [history, setHistory] = useState([]);
  const [viewing, setViewing] = useState(null);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/digest/history");
      setHistory(data.digests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const generatePreview = async () => {
    setDrafting(true);
    setDraft(null);
    try {
      const { data } = await client.get("/admin/digest/preview");
      if (data.status === "skipped_no_data") {
        toast.info(data.reason || "No delivered loads last week — nothing to summarize.");
      } else {
        setDraft(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setDrafting(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm("Send this week's digest to your email right now?")) return;
    setSending(true);
    try {
      const { data } = await client.post("/admin/digest/send");
      if (data.status === "sent") {
        toast.success(`Sent (${data.provider || "email"}) — check your inbox`);
        setDraft(null);
        await loadHistory();
      } else if (data.status === "already_sent_this_week") {
        toast.info(`Already sent this week (${data.week_key})`);
      } else if (data.status === "skipped_no_data") {
        toast.info(data.reason || "No data to summarize");
      } else {
        toast.error(data.error || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-digest-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#FF5A1F] mb-1">WEEKLY DIGEST</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            AI weekly business brief
          </h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
            Every Monday at 8am Central, GPT-5.2 reads your last 7 days of dispatch data + 90-day
            lane performance and emails you a 3-paragraph plain-English brief. Preview or send
            manually anytime.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={generatePreview} disabled={drafting}
            data-testid="digest-preview-btn"
            className="rounded-full px-4 py-2 text-xs border border-white/15 hover:border-white/30 text-neutral-300 hover:text-white inline-flex items-center gap-1.5 disabled:opacity-60">
            {drafting ? <><ArrowClockwise size={12} className="animate-spin" /> Drafting…</> :
              <><Sparkle size={12} weight="fill" /> Preview</>}
          </button>
          <button onClick={sendNow} disabled={sending}
            data-testid="digest-send-btn"
            className="btn-primary rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-60">
            {sending ? <><ArrowClockwise size={12} className="animate-spin" /> Sending…</> :
              <><PaperPlaneTilt size={12} weight="fill" /> Send now</>}
          </button>
        </div>
      </div>

      {/* Draft preview */}
      {draft && (
        <div className="card-surface p-5 space-y-4" data-testid="digest-draft-preview">
          {draft.context?.week_stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pb-4 border-b border-white/[0.06]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">Loads</div>
                <div className="text-2xl font-display font-semibold text-white">
                  {draft.context.week_stats.loads}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-0.5">Revenue</div>
                <div className="text-2xl font-display font-semibold text-emerald-400">
                  {fmtUSD(draft.context.week_stats.revenue)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] mb-0.5">$/mi</div>
                <div className="text-2xl font-display font-semibold text-[#FF5A1F]">
                  ${draft.context.week_stats.per_mile}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">Deadhead</div>
                <div className="text-2xl font-display font-semibold text-white">
                  {Math.round(draft.context.week_stats.empty_miles || 0).toLocaleString()}
                </div>
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Subject</div>
            <div className="text-sm font-medium text-white" data-testid="digest-draft-subject">
              {draft.subject}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Body preview</div>
            <div className="whitespace-pre-wrap text-sm text-neutral-300 leading-relaxed"
                 data-testid="digest-draft-body">
              {draft.body}
            </div>
          </div>
          {draft.context?.top_lanes?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                Top lanes (last 90d, min 2 loads)
              </div>
              <div className="space-y-1">
                {draft.context.top_lanes.map((l) => (
                  <div key={l.lane} className="flex items-center justify-between text-xs text-neutral-400 py-1">
                    <span className="font-mono text-white">{l.lane}</span>
                    <span>{l.count} loads · <span className="text-emerald-400">{fmtUSD(l.revenue)}</span> · <span className="text-[#FF5A1F]">${l.per_mile}/mi</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* History */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">
          Sent history
        </div>
        {loading ? (
          <div className="text-sm text-neutral-500 py-8 text-center">Loading…</div>
        ) : history.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/10 py-10 text-center text-sm text-neutral-500">
            <Newspaper size={20} className="mx-auto mb-2 text-neutral-600" />
            No digests sent yet. Click "Send now" or wait for Monday 8am Central.
          </div>
        ) : (
          <div className="space-y-2" data-testid="digest-history">
            {history.map((h) => (
              <div key={h.id}
                className="rounded-lg border border-white/10 bg-white/[0.02] p-4 flex items-center gap-3"
                data-testid={`digest-history-${h.id}`}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 shrink-0 w-20">
                  {h.week_key}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{h.subject}</div>
                  <div className="text-[11px] text-neutral-500">
                    {new Date(h.created_at).toLocaleString()} · via {h.provider}
                  </div>
                </div>
                <div className="shrink-0">
                  {h.status === "sent" ? (
                    <span className="text-emerald-400 text-xs inline-flex items-center gap-1">
                      <CheckCircle size={12} weight="fill" /> sent
                    </span>
                  ) : (
                    <span className="text-red-400 text-xs">failed</span>
                  )}
                </div>
                <button onClick={() => setViewing(h)}
                  data-testid={`digest-view-${h.id}`}
                  className="shrink-0 p-1.5 text-neutral-500 hover:text-white">
                  <Eye size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View modal */}
      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
             data-testid="digest-view-modal">
          <div className="min-h-full flex items-start justify-center py-8">
            <div className="w-full max-w-2xl bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    {viewing.week_key} · {new Date(viewing.created_at).toLocaleDateString()}
                  </div>
                  <div className="text-sm font-medium text-white mt-1">{viewing.subject}</div>
                </div>
                <button onClick={() => setViewing(null)}
                  className="text-neutral-500 hover:text-white p-1"
                  data-testid="digest-modal-close">
                  <XIcon size={18} />
                </button>
              </div>
              <div className="px-6 py-5 whitespace-pre-wrap text-sm text-neutral-300 leading-relaxed">
                {viewing.body}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
